import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ChevronRight from '@mui/icons-material/ChevronRight';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import AddOutlined from '@mui/icons-material/AddOutlined';
import api from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import AppShell from '../../components/layout/AppShell';
import {
  Button,
  PageHeader,
  PageLoader,
  EmptyState,
  Alert,
  Card,
  Input,
  Select,
  Badge,
} from '../../components/ui';

const SHIFT_OPTIONS = [
  { value: '', label: 'All' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
];

function ViewReports() {
  const { role } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(sessionStorage.getItem('activeTab') || 'shift');
  const [shiftReports, setShiftReports] = useState([]);
  const [monthlyReports, setMonthlyReports] = useState([]);
  const [searchInCharge, setSearchInCharge] = useState(sessionStorage.getItem('searchInCharge') || '');
  const [searchDate, setSearchDate] = useState(sessionStorage.getItem('searchDate') || '');
  const [searchShift, setSearchShift] = useState(sessionStorage.getItem('searchShift') || '');
  const [searchSection, setSearchSection] = useState(sessionStorage.getItem('searchSection') || '');
  const [searchClientName, setSearchClientName] = useState(
    sessionStorage.getItem('searchClientName') || ''
  );
  const [filteredReports, setFilteredReports] = useState([]);
  const [filteredMonthlyReports, setFilteredMonthlyReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(Number(sessionStorage.getItem('currentPage')) || 1);
  const [loading, setLoading] = useState(true);
  const [loadingTooLong, setLoadingTooLong] = useState(false);
  const reportsPerPage = 10;
  const navigate = useNavigate();

  // Non-admin users can't reach the monthly tab.
  useEffect(() => {
    if (role !== 'admin' && activeTab === 'monthly') {
      setActiveTab('shift');
      sessionStorage.setItem('activeTab', 'shift');
    }
  }, [role, activeTab]);

  useEffect(() => {
    sessionStorage.setItem('activeTab', activeTab);
    sessionStorage.setItem('searchInCharge', searchInCharge);
    sessionStorage.setItem('searchDate', searchDate);
    sessionStorage.setItem('searchShift', searchShift);
    sessionStorage.setItem('searchSection', searchSection);
    sessionStorage.setItem('searchClientName', searchClientName);
    sessionStorage.setItem('currentPage', currentPage);
  }, [activeTab, searchInCharge, searchDate, searchShift, searchSection, searchClientName, currentPage]);

  useEffect(() => {
    const fetchShiftReports = async () => {
      setLoading(true);
      try {
        const response = await api.get('/shift-report/shift-reports');
        const sortedReports = response.data.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          if (dateA > dateB) return -1;
          if (dateA < dateB) return 1;
          if (a.shift > b.shift) return -1;
          if (a.shift < b.shift) return 1;
          return 0;
        });
        setShiftReports(sortedReports);
      } catch (error) {
        console.error('Error fetching shift reports:', error);
      }
    };

    const fetchMonthlyReports = async () => {
      if (role === 'admin') {
        try {
          const response = await api.get('/monthly-report/monthly-reports');
          const sorted = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
          setMonthlyReports(sorted);
        } catch (error) {
          console.error('Error fetching monthly reports:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchShiftReports();
    fetchMonthlyReports();
  }, [role]);

  useEffect(() => {
    if (!loading) {
      setLoadingTooLong(false);
      return undefined;
    }
    const timeoutId = setTimeout(() => setLoadingTooLong(true), 10000);
    return () => clearTimeout(timeoutId);
  }, [loading]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userId');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('userId');
    navigate('/login');
  };

  useEffect(() => {
    if (activeTab === 'shift') {
      setFilteredReports(
        shiftReports.filter((report) => {
          const inChargeMatch = searchInCharge
            ? report.inCharge?.toLowerCase().includes(searchInCharge.toLowerCase())
            : true;
          const dateMatch = searchDate
            ? new Date(report.date).toISOString().split('T')[0] === searchDate
            : true;
          const shiftMatch = searchShift ? report.shift?.toString() === searchShift : true;
          const sectionMatch = searchSection ? report.scrArea === searchSection : true;
          return inChargeMatch && dateMatch && shiftMatch && sectionMatch;
        })
      );
    } else {
      setFilteredMonthlyReports(
        monthlyReports.filter((report) => {
          const clientMatch = searchClientName
            ? report.clientName?.toLowerCase().includes(searchClientName.toLowerCase())
            : true;
          const dateMatch = searchDate
            ? new Date(report.date).toISOString().split('T')[0] === searchDate
            : true;
          return clientMatch && dateMatch;
        })
      );
    }
  }, [
    searchInCharge,
    searchDate,
    searchShift,
    searchSection,
    searchClientName,
    activeTab,
    shiftReports,
    monthlyReports,
  ]);

  const sectionOptions = [
    ...new Set(
      shiftReports
        .map((report) => (typeof report.scrArea === 'string' ? report.scrArea.trim() : ''))
        .filter(Boolean)
    ),
  ].sort((a, b) => a.localeCompare(b));

  const indexOfLastReport = currentPage * reportsPerPage;
  const indexOfFirstReport = indexOfLastReport - reportsPerPage;
  const activeList = activeTab === 'shift' ? filteredReports : filteredMonthlyReports;
  const currentReports = activeList.slice(indexOfFirstReport, indexOfLastReport);
  const totalPages = Math.ceil(activeList.length / reportsPerPage) || 1;

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Return to the top of the new page. The previous behaviour scrolled to the
    // bottom, which landed the reader on the pagination bar rather than results.
    window.scrollTo({ top: 0, behavior: 'smooth' });
    sessionStorage.setItem('currentPage', pageNumber);
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    sessionStorage.setItem('activeTab', tab);
    sessionStorage.setItem('currentPage', 1);
  };

  const handleReportClick = (reportId) => {
    if (activeTab === 'monthly' && role !== 'admin') {
      toast.error('You do not have permission to access monthly reports.');
      return;
    }

    const report = activeList.find((r) => r._id === reportId);
    if (report) {
      navigate(`/view-${activeTab === 'shift' ? 'shift' : 'monthly'}-report/${reportId}`, {
        state: { report },
      });
    } else {
      console.error('Report not found');
    }
  };

  const hasFilters =
    activeTab === 'shift'
      ? Boolean(searchInCharge || searchDate || searchShift || searchSection)
      : Boolean(searchClientName || searchDate);

  const clearFilters = () => {
    setSearchInCharge('');
    setSearchDate('');
    setSearchShift('');
    setSearchSection('');
    setSearchClientName('');
    setCurrentPage(1);
  };

  const tabClasses = (isActive) =>
    [
      'min-h-11 flex-1 cursor-pointer border-b-2 px-4 text-sm font-medium transition-colors duration-fast sm:flex-none',
      isActive
        ? 'border-accent text-accent'
        : 'border-transparent text-muted-fg hover:border-border-strong hover:text-foreground',
    ].join(' ');

  return (
    <>
      <PageHeader
        sticky={false}
        title="Reports"
        subtitle="Shift and monthly maintenance reports"
        backTo="/"
        actions={
          <Button icon={AddOutlined} onClick={() => navigate('/add-shift-report')}>
            New shift report
          </Button>
        }
      />

      <AppShell>
        {/* Admins get both report types; everyone else only ever sees shift reports,
            so no tab strip is rendered for them. */}
        {role === 'admin' && (
          <div className="mb-5 flex border-b border-border">
            <button type="button" className={tabClasses(activeTab === 'shift')} onClick={() => handleTabSwitch('shift')}>
              Shift reports
            </button>
            <button
              type="button"
              className={tabClasses(activeTab === 'monthly')}
              onClick={() => handleTabSwitch('monthly')}
            >
              Monthly reports
            </button>
          </div>
        )}

        <div className="mb-5 rounded-lg border border-border bg-surface p-4 shadow-sm">
          {activeTab === 'shift' ? (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <Input
                  label="In-charge"
                  type="search"
                  value={searchInCharge}
                  onChange={(e) => setSearchInCharge(e.target.value)}
                  placeholder="Search by name"
                />
                <Input
                  label="Date"
                  type="date"
                  value={searchDate}
                  onChange={(e) => setSearchDate(e.target.value)}
                />
                <Select
                  label="Section"
                  value={searchSection}
                  onChange={(e) => setSearchSection(e.target.value)}
                >
                  <option value="">All sections</option>
                  {sectionOptions.map((section) => (
                    <option key={section} value={section}>{section}</option>
                  ))}
                </Select>
              </div>

              <fieldset className="mt-4">
                <legend className="mb-1.5 text-sm font-medium text-foreground">Shift</legend>
                <div className="inline-flex overflow-hidden rounded-md border border-border-strong">
                  {SHIFT_OPTIONS.map((option, i) => {
                    const selected = searchShift === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setSearchShift(option.value)}
                        className={[
                          'min-h-11 min-w-[3.5rem] cursor-pointer px-4 text-sm font-medium transition-colors duration-fast',
                          i > 0 ? 'border-l border-border-strong' : '',
                          selected
                            ? 'bg-accent text-accent-fg'
                            : 'bg-surface text-muted-fg hover:bg-muted hover:text-foreground',
                        ].join(' ')}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            </>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Client name"
                type="search"
                value={searchClientName}
                onChange={(e) => setSearchClientName(e.target.value)}
                placeholder="Search by client"
              />
              <Input
                label="Date"
                type="date"
                value={searchDate}
                onChange={(e) => setSearchDate(e.target.value)}
              />
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border pt-3 text-sm text-muted-fg">
            <span>
              <span className="tabular font-medium text-foreground">{activeList.length}</span>{' '}
              {activeList.length === 1 ? 'report' : 'reports'}
            </span>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <>
            <PageLoader label="Loading reports…" />
            {loadingTooLong && (
              <Alert
                variant="warning"
                title="Taking longer than usual"
                className="mx-auto max-w-md"
                action={
                  <Button variant="secondary" size="sm" onClick={handleLogout}>
                    Log out and retry
                  </Button>
                }
              >
                Your session may have expired.
              </Alert>
            )}
          </>
        ) : currentReports.length === 0 ? (
          <EmptyState
            icon={DescriptionOutlined}
            title="No reports found"
            description={
              hasFilters
                ? 'No reports match the current filters.'
                : 'Nothing has been filed yet.'
            }
            action={
              hasFilters ? (
                <Button variant="secondary" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : (
                <Button icon={AddOutlined} onClick={() => navigate('/add-shift-report')}>
                  Create a shift report
                </Button>
              )
            }
          />
        ) : (
          <>
            <ul className="space-y-3">
              {currentReports.map((report, index) => {
                const reportNumber = activeList.length - indexOfFirstReport - index;
                return (
                  <li key={report._id}>
                    <Card interactive onClick={() => handleReportClick(report._id)} className="group">
                      <div className="flex items-center gap-3 p-4">
                        <span className="tabular flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-sm font-semibold text-muted-fg">
                          {reportNumber}
                        </span>

                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-foreground">
                            {activeTab === 'shift' ? report.inCharge : report.clientName}
                          </p>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-fg">
                            <span>{new Date(report.date).toLocaleDateString('en-GB')}</span>
                            {activeTab === 'shift' && (
                              <>
                                <Badge variant="neutral" size="sm">Shift {report.shift}</Badge>
                                {report.scrArea && (
                                  <Badge variant="neutral" size="sm">{report.scrArea}</Badge>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        <ChevronRight
                          style={{ fontSize: 20 }}
                          aria-hidden="true"
                          className="shrink-0 text-muted-fg transition-transform duration-base group-hover:translate-x-1 group-hover:text-accent"
                        />
                      </div>
                    </Card>
                  </li>
                );
              })}
            </ul>

            {totalPages > 1 && (
              <nav aria-label="Pagination" className="mt-8 flex flex-col items-center gap-3">
                <p className="text-sm text-muted-fg">
                  Page <span className="tabular font-medium text-foreground">{currentPage}</span> of{' '}
                  <span className="tabular font-medium text-foreground">{totalPages}</span>
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Prev
                  </Button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                    let startPage = Math.max(1, currentPage - 2);
                    let endPage = startPage + 4;
                    if (endPage > totalPages) {
                      endPage = totalPages;
                      startPage = Math.max(1, endPage - 4);
                    }
                    const page = startPage + index;
                    if (page > totalPages) return null;

                    const isCurrent = currentPage === page;
                    return (
                      <button
                        key={page}
                        type="button"
                        onClick={() => handlePageChange(page)}
                        aria-current={isCurrent ? 'page' : undefined}
                        className={[
                          'tabular h-11 w-11 cursor-pointer rounded-md text-sm font-medium transition-colors duration-fast',
                          isCurrent
                            ? 'bg-accent text-accent-fg'
                            : 'text-muted-fg hover:bg-muted hover:text-foreground',
                        ].join(' ')}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </nav>
            )}
          </>
        )}
      </AppShell>
    </>
  );
}

export default ViewReports;
